import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { GripVertical, Trash2, ArrowLeftRight, Save, Music, Radio, Library, UploadCloud } from "lucide-react";
import AudioUploadButton from "@/components/AudioUploadButton";

const BLOCK_COUNT = 8;
const ON_DEMAND_KEY = "on_demand";
const LIBRARY_KEY = "library";

// Coerce a stored block into normalized shape.
const normalizeBlock = (raw) => {
  if (Array.isArray(raw)) return { name: "", synopsis: "", tracks: raw.map(coerceTrack) };
  if (raw && typeof raw === "object") {
    return {
      name: raw.name || "",
      synopsis: raw.synopsis || "",
      tracks: (Array.isArray(raw.tracks) ? raw.tracks : []).map(coerceTrack),
    };
  }
  return { name: "", synopsis: "", tracks: [] };
};

const coerceTrack = (t) => {
  if (typeof t === "string") return { url: t, title: "", artist: "" };
  return { url: t?.url || "", title: t?.title || "", artist: t?.artist || "" };
};

const displayTitle = (t) => {
  if (t.title && t.title.trim()) return t.title.trim();
  // Fallback: extract last URL segment / query
  try {
    const u = new URL(t.url);
    const seg = u.pathname.split("/").filter(Boolean).pop() || u.hostname;
    return decodeURIComponent(seg);
  } catch (_e) {
    return t.url || "(no url)";
  }
};

const badgeFor = (url) => {
  if (!url) return { label: "?", cls: "bg-white/10 text-[#A1A1AA]" };
  if (url.includes("youtu.be") || url.includes("youtube.com")) return { label: "YT", cls: "bg-red-500/20 text-red-400" };
  if (url.includes("/api/uploads/audio/")) return { label: "MP3", cls: "bg-[#A855F7]/20 text-[#A855F7]" };
  return { label: "URL", cls: "bg-white/10 text-[#FAFAFA]" };
};

const columnLabel = (key) => {
  if (key === ON_DEMAND_KEY) return "On Demand";
  if (key === LIBRARY_KEY) return "Library (Uploads)";
  const idx = Number(key.replace("b", ""));
  const startHour = 7 + idx * 2;
  return `Block ${idx + 1} · ${String(startHour).padStart(2, "0")}:00`;
};

const AdminTrackRouter = ({ settings, setSettings }) => {
  const [dragging, setDragging] = useState(null); // { fromKey, index }
  const [dropTarget, setDropTarget] = useState(null); // key
  const [saving, setSaving] = useState(false);
  const [library, setLibrary] = useState([]);

  // Load the full uploaded audio library — the "source palette" of tracks
  // admins can drag into any block or the On Demand column. Also refetched
  // after every upload so the new track appears without a page reload.
  const refreshLibrary = () => {
    api
      .get("/uploads/library")
      .then((r) => setLibrary(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  };
  useEffect(() => {
    refreshLibrary();
  }, []);

  const dc = settings?.deep_corners || { enabled: true, intermission_url: "", blocks: [] };
  const od = settings?.on_demand || { tracks: [] };

  const blocks = useMemo(
    () => Array.from({ length: BLOCK_COUNT }, (_, i) => normalizeBlock(dc.blocks?.[i])),
    [JSON.stringify(dc.blocks)]
  );
  const onDemandTracks = useMemo(
    () => (Array.isArray(od.tracks) ? od.tracks : []).map(coerceTrack),
    [JSON.stringify(od.tracks)]
  );

  const columns = useMemo(() => {
    const cols = {};
    cols[LIBRARY_KEY] = library.map((t) => ({
      url: t.url,
      title: t.title || "",
      artist: t.artist || "",
    }));
    for (let i = 0; i < BLOCK_COUNT; i++) cols[`b${i}`] = blocks[i].tracks;
    cols[ON_DEMAND_KEY] = onDemandTracks;
    return cols;
  }, [blocks, onDemandTracks, library]);

  const persist = (nextCols) => {
    const nextBlocks = Array.from({ length: BLOCK_COUNT }, (_, i) => ({
      ...blocks[i],
      tracks: nextCols[`b${i}`],
    }));
    setSettings({
      ...settings,
      deep_corners: { ...dc, blocks: nextBlocks },
      on_demand: { ...od, tracks: nextCols[ON_DEMAND_KEY] },
    });
    // Note: LIBRARY_KEY is the read-only palette — never persisted back.
  };

  const moveTrack = (fromKey, fromIdx, toKey, mode = "move") => {
    if (!(fromKey in columns) || !(toKey in columns)) return;
    if (toKey === LIBRARY_KEY) return; // Library is source-only — drops rejected.
    // Auto-copy semantics:
    //   • Dragging OUT of Library → always copy (palette stays intact).
    //   • Dragging block/on-demand → On Demand → always copy so the track stays
    //     in its original block (curators want tracks in BOTH places).
    let effective = mode;
    if (fromKey === LIBRARY_KEY) effective = "copy";
    if (toKey === ON_DEMAND_KEY && fromKey !== ON_DEMAND_KEY) effective = "copy";
    const source = [...columns[fromKey]];
    const target = fromKey === toKey ? source : [...columns[toKey]];
    const [item] = source.splice(fromIdx, 1);
    if (!item) return;
    if (effective === "copy") {
      // Restore source
      source.splice(fromIdx, 0, item);
    }
    if (fromKey === toKey) {
      source.push(item);
      persist({ ...columns, [fromKey]: source });
    } else {
      target.push(item);
      if (fromKey === LIBRARY_KEY) {
        // Only touch the destination — library is derived from uploads, not saved.
        persist({ ...columns, [toKey]: target });
      } else if (effective === "copy") {
        // Source unchanged (block keeps the track), only destination gains a copy.
        persist({ ...columns, [toKey]: target });
      } else {
        persist({ ...columns, [fromKey]: source, [toKey]: target });
      }
    }
  };

  const removeTrack = (colKey, idx) => {
    const next = [...columns[colKey]];
    next.splice(idx, 1);
    persist({ ...columns, [colKey]: next });
  };

  const onDragStart = (fromKey, index, event) => {
    setDragging({ fromKey, index });
    try {
      event.dataTransfer.effectAllowed = event.altKey ? "copy" : "move";
      event.dataTransfer.setData("text/plain", `${fromKey}:${index}`);
    } catch (_e) { /* noop */ }
  };

  const onDragOverCol = (key, e) => {
    e.preventDefault();
    if (dropTarget !== key) setDropTarget(key);
    try { e.dataTransfer.dropEffect = e.altKey ? "copy" : "move"; } catch (_e) { /* noop */ }
  };

  const onDropCol = (toKey, e) => {
    e.preventDefault();
    setDropTarget(null);
    if (!dragging) return;
    const mode = e.altKey ? "copy" : "move";
    moveTrack(dragging.fromKey, dragging.index, toKey, mode);
    setDragging(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", settings);
      toast.success("Track routing saved");
    } catch (e) {
      toast.error("Failed to save track routing");
    } finally {
      setSaving(false);
    }
  };

  const totalTracks = Object.entries(columns).reduce(
    (n, [k, arr]) => (k === LIBRARY_KEY ? n : n + arr.length),
    0
  );

  return (
    <div data-testid="admin-track-router">
      <div className="flex flex-col gap-3 border-b border-white/10 pb-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="font-[Unbounded,sans-serif] text-2xl font-bold uppercase tracking-tight">
            Track Router
          </h2>
          <p className="mt-2 max-w-2xl font-mono text-sm font-bold leading-relaxed text-[#A1A1AA]">
            Drag any track between the 8 daily blocks or the On Demand player.
            <span className="text-[#A855F7]"> Dropping into On Demand always copies</span> — the source block keeps its track.
            Hold <kbd className="mx-1 border border-white/15 bg-black px-1 py-0.5 font-mono text-xs">Alt</kbd> to copy between blocks. Save to persist.
            <span className="ml-2 font-bold text-[#FAFAFA]">{totalTracks} tracks routed.</span>
          </p>
        </div>
        <button
          type="button"
          onClick={save}
          data-testid="track-router-save"
          disabled={saving}
          className="inline-flex items-center gap-2 border border-[#A855F7] bg-[#A855F7] px-5 py-3 font-mono text-sm font-bold uppercase tracking-[0.28em] text-black transition-colors hover:bg-transparent hover:text-[#A855F7] disabled:opacity-50"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save Routing"}
        </button>
      </div>

      {/* Library palette — full list of uploaded audio files, source-only */}
      {(() => {
        const key = LIBRARY_KEY;
        const list = columns[key];
        const highlighted = dropTarget === key;
        const handleUpload = (_url, _data) => {
          // The auto-listing on /on-demand/tracks will pick up the new file automatically
          // — we just refresh the local Library palette so admins see it right away.
          refreshLibrary();
          toast.success("Uploaded — now available in the Library and On Demand");
        };
        return (
          <div
            data-testid={`router-column-${key}`}
            className={`mt-6 flex flex-col border border-[#A855F7]/40 bg-black/60 p-4 ${
              highlighted ? "outline outline-2 outline-[#A855F7]" : ""
            }`}
          >
            <div className="flex flex-col gap-3 border-b border-white/10 pb-3 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-2 font-mono text-sm font-bold uppercase tracking-[0.22em] text-[#A855F7]">
                <Library size={14} />
                {columnLabel(key)}
                <span className="ml-2 font-mono text-xs font-bold text-[#A1A1AA]">
                  · drag any track into a block or On Demand
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs font-bold text-[#A1A1AA]">{list.length} tracks</span>
                <AudioUploadButton
                  testid="library-upload-btn"
                  label="Upload to On Demand"
                  onUploaded={handleUpload}
                />
              </div>
            </div>
            <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid={`router-column-list-${key}`}>
              {list.length === 0 ? (
                <li className="col-span-full py-6 text-center font-mono text-xs font-bold uppercase tracking-[0.22em] text-[#52525B]">
                  No uploads yet. Upload tracks via Deep Corners → Add URL → Upload button.
                </li>
              ) : (
                list.map((t, i) => {
                  const bd = badgeFor(t.url);
                  const isBeingDragged = dragging?.fromKey === key && dragging?.index === i;
                  return (
                    <li
                      key={`${key}-${i}-${t.url}`}
                      draggable
                      onDragStart={(e) => onDragStart(key, i, e)}
                      onDragEnd={() => { setDragging(null); setDropTarget(null); }}
                      data-testid={`router-track-${key}-${i}`}
                      className={`group flex items-center gap-2 border border-white/10 bg-black/70 px-2 py-2 transition-opacity ${
                        isBeingDragged ? "opacity-40" : "hover:border-[#A855F7]/60"
                      }`}
                    >
                      <GripVertical size={14} className="flex-shrink-0 cursor-grab text-[#52525B] active:cursor-grabbing" />
                      <span className={`flex-shrink-0 rounded-sm px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.1em] ${bd.cls}`}>
                        {bd.label}
                      </span>
                      <span className="min-w-0 flex-1 truncate font-mono text-xs font-bold text-[#FAFAFA]" title={t.url}>
                        {displayTitle(t)}
                      </span>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        );
      })()}

      <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3">
        {Object.keys(columns).filter((k) => k !== LIBRARY_KEY).map((key) => {
          const isOD = key === ON_DEMAND_KEY;
          const list = columns[key];
          const highlighted = dropTarget === key;
          return (
            <div
              key={key}
              data-testid={`router-column-${key}`}
              onDragOver={(e) => onDragOverCol(key, e)}
              onDragLeave={() => setDropTarget((k) => (k === key ? null : k))}
              onDrop={(e) => onDropCol(key, e)}
              className={`flex min-h-[220px] flex-col border ${
                isOD ? "border-[#A855F7]/60" : "border-white/10"
              } ${highlighted ? "bg-[#A855F7]/10 outline outline-2 outline-[#A855F7]" : "bg-black/50"} p-3 transition-colors`}
            >
              <div className="flex items-center justify-between border-b border-white/10 pb-2">
                <div className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.22em]">
                  {isOD ? <Music size={12} className="text-[#A855F7]" /> : <Radio size={12} className="text-[#A855F7]" />}
                  <span className={isOD ? "text-[#A855F7]" : "text-[#FAFAFA]"}>
                    {columnLabel(key)}
                  </span>
                </div>
                <span className="font-mono text-xs font-bold text-[#A1A1AA]">{list.length}</span>
              </div>

              <ul className="mt-3 flex flex-1 flex-col gap-2" data-testid={`router-column-list-${key}`}>
                {list.length === 0 ? (
                  <li className="flex flex-1 items-center justify-center py-6 text-center">
                    <span className="font-mono text-xs font-bold uppercase tracking-[0.22em] text-[#52525B]">
                      <ArrowLeftRight size={14} className="mx-auto mb-1" />
                      Drop tracks here
                    </span>
                  </li>
                ) : (
                  list.map((t, i) => {
                    const bd = badgeFor(t.url);
                    const isBeingDragged = dragging?.fromKey === key && dragging?.index === i;
                    return (
                      <li
                        key={`${key}-${i}-${t.url}`}
                        draggable
                        onDragStart={(e) => onDragStart(key, i, e)}
                        onDragEnd={() => { setDragging(null); setDropTarget(null); }}
                        data-testid={`router-track-${key}-${i}`}
                        className={`group flex items-center gap-2 border border-white/10 bg-black/60 px-2 py-2 transition-opacity ${
                          isBeingDragged ? "opacity-40" : "hover:border-white/25"
                        }`}
                      >
                        <GripVertical size={14} className="flex-shrink-0 cursor-grab text-[#52525B] active:cursor-grabbing" />
                        <span className={`flex-shrink-0 rounded-sm px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.1em] ${bd.cls}`}>
                          {bd.label}
                        </span>
                        <span className="min-w-0 flex-1 truncate font-mono text-xs font-bold text-[#FAFAFA]" title={t.url}>
                          {displayTitle(t)}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeTrack(key, i)}
                          data-testid={`router-track-remove-${key}-${i}`}
                          aria-label="Remove"
                          className="flex-shrink-0 text-[#52525B] opacity-0 transition-opacity hover:text-[#FF3B30] group-hover:opacity-100"
                        >
                          <Trash2 size={12} />
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex items-center gap-2 border border-white/10 bg-black/40 p-3 font-mono text-xs font-bold text-[#A1A1AA]">
        <span className="text-[#A855F7]">Tip:</span>
        Changes are staged locally — hit <span className="text-[#FAFAFA]">Save Routing</span> to persist. Titles/artists are edited in the main Deep Corners section.
      </div>
    </div>
  );
};

export default AdminTrackRouter;
